/**
 * Ce que chaque modèle prédit **au réglage courant**, en chiffres.
 *
 * Le graphe montre un domaine entier, le schéma montre une géométrie ; ni l'un
 * ni l'autre ne donne une valeur qu'on puisse lire. C'est pourtant ce que la
 * règle §3 exige : toute conclusion lisible dans une figure doit l'être dans
 * une table.
 *
 * Le rendu est générique — une légende, des colonnes, des lignes. Ce sont les
 * expériences qui savent quoi mettre dedans : une longueur d'ombre et un
 * rapport ombre / bâton pour l'une, une durée de chute et une vitesse
 * d'impact pour l'autre. Aucune ne peut y glisser un classement : la table
 * n'a pas de colonne « verdict », et sa légende dit l'inverse.
 */

import type { FreeHypotheses } from "../core/models.js";
import type { ReadoutTable } from "../experiment/types.js";
import { valueIn } from "../core/units.js";
import { fr } from "../i18n/fr.js";
import { escape, escapeAttribute, formatNumber } from "./format.js";

/**
 * « 2 » et « une par relevé » ne sont pas deux nombres, ce sont deux natures.
 * Les rendre au même endroit garde la distinction visible partout où le compte
 * s'affiche.
 */
export function freeHypothesesLabel(free: FreeHypotheses): string {
  return free.kind === "perObservation"
    ? fr.perObservation
    : formatNumber(valueIn(free.count, "1"), 0);
}

export function renderReadout(table: ReadoutTable): string {
  const head = table.columns
    .map((column) => `<th scope="col">${escape(column)}</th>`)
    .join("");

  const rows = table.rows
    .map((row) => {
      const cells = row.cells.map((cell) => `<td>${escape(cell)}</td>`).join("");
      return `<tr><th scope="row">${escape(row.header)}</th>${cells}</tr>`;
    })
    .join("");

  return [
    `<div class="verlet-table-scroll" role="region" tabindex="0"`,
    ` aria-label="${escapeAttribute(table.caption)}">`,
    `<table>`,
    `<caption>${escape(table.caption)}</caption>`,
    `<thead><tr>${head}</tr></thead>`,
    `<tbody>${rows}</tbody>`,
    `</table>`,
    `</div>`,
  ].join("");
}
